// Generated with util/create-component.js
import React from 'react';
import { DatePicker, Row, Col } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { DATE_API_FORMAT } from 'constants/date_formats';

import { IGiftCardDatePickerProps } from './gift_card_date_picker.types';
import styles from './gift_card_date_picker.module.scss';

const GiftCardDatePicker: React.FC<IGiftCardDatePickerProps> = ({
  dates,
  maxDays,
  maxDate,
  closedDatesPerRate,
  onDatesChange,
}) => {
  const { t } = useTranslation();

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const checkinRef: React.RefObject<any> = React.createRef();

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const checkoutRef: React.RefObject<any> = React.createRef();

  const handleDateChange = (date: Dayjs | null, index: number) => {
    checkinRef?.current?.blur();
    checkoutRef?.current?.blur();

    if (!date) {
      onDatesChange([null, null]);
      return;
    }
    if (date && index === 0) {
      const checkoutDate = dayjs(date.toISOString()).add(maxDays, 'day');
      onDatesChange([date, checkoutDate]);
      return;
    }
    if (date && index === 1 && !dates?.[0]) {
      const checkinDate = dayjs(date.toISOString()).add(-maxDays, 'day');
      onDatesChange([checkinDate, date]);
      return;
    }
  };

  const isDateClosedForCheckin = (date: Dayjs) => {
    if (closedDatesPerRate) {
      let isClosedRange = true;

      for (const ratePlanId in closedDatesPerRate) {
        if (ratePlanId === 'type') {
          continue;
        }

        const hasClosedDate = closedDatesPerRate[ratePlanId].closed.find(d => {
          if (d === dayjs(date).format(DATE_API_FORMAT)) return true;
          return (
            0 <= dayjs(d, DATE_API_FORMAT).diff(dayjs(date), 'day') &&
            dayjs(d, DATE_API_FORMAT).diff(dayjs(date), 'day') < maxDays - 1
          );
        });

        if (!hasClosedDate) {
          isClosedRange = false;
          break;
        }
      }
      return isClosedRange;
    }

    return false;
  };

  const isDateClosedForCheckout = (date: Dayjs) => {
    if (closedDatesPerRate) {
      let isClosedRange = true;

      for (const ratePlanId in closedDatesPerRate) {
        if (ratePlanId === 'type') {
          continue;
        }

        const hasClosedDate = closedDatesPerRate[ratePlanId].closed.find(d => {
          const diff = dayjs(date).diff(dayjs(d, DATE_API_FORMAT), 'day');
          if (0 < diff && diff < maxDays) return d;
        });

        if (!hasClosedDate) {
          isClosedRange = false;
          break;
        }
      }
      return isClosedRange;
    }

    return false;
  };

  const isPastDate = (date: Dayjs) => 0 < dayjs().diff(date, 'day');

  const isNotAvailableDate = (date: Dayjs) => {
    if (maxDate && dayjs(maxDate).diff(date, 'day') < 0) {
      return true;
    }

    return false;
  };

  const isCheckinDateDisabled = (date: Dayjs) => {
    if (isPastDate(date)) return true;

    if (dates?.[0] && dayjs(dates[0]).diff(date, 'day') === 0) return true;

    if (isDateClosedForCheckin(date)) return true;

    if (isNotAvailableDate(date)) return true;

    return false;
  };

  const isCheckoutDateDisabled = (date: Dayjs) => {
    if (isPastDate(date)) return true;

    if (isDateClosedForCheckout(date)) return true;
    if (isNotAvailableDate(date)) return true;

    return false;
  };

  return (
    <div data-testid="GiftCardDatePicker" className={styles.root}>
      <Row>
        <Col sm={12} xs={24} className={styles.datePickerContainer}>
          <div className={styles.datePickerLabel}>{t('hotel_page.checkin_label')}</div>
          <DatePicker
            key="Check-In"
            size="large"
            defaultValue={dates?.[0] || undefined}
            placeholder={t('general.select_your_date')}
            onChange={date => handleDateChange(date, 0)}
            value={dates?.[0]}
            disabledDate={isCheckinDateDisabled}
            className={styles.datePicker}
            ref={checkinRef}
          />
        </Col>
        <Col sm={12} xs={24}>
          <div className={styles.datePickerLabel}>{t('hotel_page.checkout_label')}</div>
          <DatePicker
            key="Check-Out"
            size="large"
            defaultValue={dates?.[1] || undefined}
            value={dates?.[1]}
            placeholder={t('general.select_your_date')}
            onChange={date => handleDateChange(date, 1)}
            disabledDate={isCheckoutDateDisabled}
            className={styles.datePicker}
            ref={checkoutRef}
          />
        </Col>
      </Row>
    </div>
  );
};

export default GiftCardDatePicker;
